import { RoverBrand } from '@rover/types/src/RoverBrand';

type RoverDomain = { value: string; regex: RegExp };

export enum RoverDomainValue {
  RoverCom = 'rover.com',
  RoverStaging = 'roverstaging.com',
}

export enum CatInAFlatDomain {
  Local = 'ciaf.local',
  Dev = 'catinaflat.dev',
  Com = 'catinaflat.com',
  CoUk = 'catinaflat.co.uk',
  Ca = 'catinaflat.ca',
  De = 'catinaflat.de',
  Es = 'catinaflat.es',
  Fr = 'catinaflat.fr',
  Ie = 'catinaflat.ie',
  Be = 'catinaflat.be',
  At = 'catinaflat.at',
  Nl = 'catinaflat.nl',
  Ch = 'catinaflat.ch',
}

export enum DogBuddyDomain {
  Com = 'dogbuddy.com',
}

/**
 * Map of brand-specific domains with their regex patterns.
 * Used for host header passthrough validation and brand detection.
 */
export const BRAND_DOMAIN_MAP: Record<RoverBrand, RoverDomain[]> = {
  [RoverBrand.Rover]: [
    { value: RoverDomainValue.RoverCom, regex: /^(.*\.)?rover\.com$/i },
    { value: RoverDomainValue.RoverStaging, regex: /^(.*\.)?roverstaging\.com$/i },
  ],
  [RoverBrand.CatInAFlat]: [
    { value: CatInAFlatDomain.Com, regex: /^(.*\.)?catinaflat\.com$/i },
    { value: CatInAFlatDomain.CoUk, regex: /^(.*\.)?catinaflat\.co\.uk$/i },
    { value: CatInAFlatDomain.Ca, regex: /^(.*\.)?catinaflat\.ca$/i },
    { value: CatInAFlatDomain.De, regex: /^(.*\.)?catinaflat\.de$/i },
    { value: CatInAFlatDomain.Es, regex: /^(.*\.)?catinaflat\.es$/i },
    { value: CatInAFlatDomain.Fr, regex: /^(.*\.)?catinaflat\.fr$/i },
    { value: CatInAFlatDomain.Ie, regex: /^(.*\.)?catinaflat\.ie$/i },
    { value: CatInAFlatDomain.Be, regex: /^(.*\.)?catinaflat\.be$/i },
    { value: CatInAFlatDomain.At, regex: /^(.*\.)?catinaflat\.at$/i },
    { value: CatInAFlatDomain.Nl, regex: /^(.*\.)?catinaflat\.nl$/i },
    { value: CatInAFlatDomain.Ch, regex: /^(.*\.)?catinaflat\.ch$/i },
    { value: CatInAFlatDomain.Dev, regex: /^(.*\.)?catinaflat\.dev$/i },
  ],
  [RoverBrand.DogBuddy]: [{ value: DogBuddyDomain.Com, regex: /^(.*\.)?dogbuddy\.com$/i }],
};

/**
 * Generates a combined regex pattern from all brand domains in BRAND_DOMAIN_MAP.
 * Useful for validating host headers against all known Rover brand domains.
 */
export const createHostHeaderPassthroughRegex = (): RegExp => {
  const allDomains = Object.values(BRAND_DOMAIN_MAP).flat();
  const pattern = allDomains.map((domain) => domain.regex.source).join('|');
  return new RegExp(`(${pattern})`, 'i');
};

export const HOST_HEADER_PASSTHROUGH_REGEX = createHostHeaderPassthroughRegex();

// Cat in a Flat domains
export const CAT_IN_A_FLAT_DOMAINS: CatInAFlatDomain[] = [
  CatInAFlatDomain.Local,
  CatInAFlatDomain.Dev,
  CatInAFlatDomain.Com,
  CatInAFlatDomain.CoUk,
  CatInAFlatDomain.De,
  CatInAFlatDomain.Es,
  CatInAFlatDomain.Fr,
  CatInAFlatDomain.Ie,
  CatInAFlatDomain.Be,
  CatInAFlatDomain.At,
  CatInAFlatDomain.Nl,
  CatInAFlatDomain.Ch,
  CatInAFlatDomain.Ca,
];

// DogBuddy domains
export const DOG_BUDDY_DOMAINS = [
  'dogbuddy.local', // Local development
  'us.dogbuddy.local', // Local development
  'dogbuddy.dev', // Staging
  'us.dogbuddy.dev', // Staging
  'us.dogbuddy.com', // Production - US
];
